import React from 'react';
import Avatar         from '../components/ui/Avatar';
import Icon           from '../components/ui/Icon';
import Badge          from '../components/ui/Badge';
import PullToRefresh  from '../components/ui/PullToRefresh';
import { useAuth }            from '../context/AuthContext';
import { useApp }             from '../context/AppContext';
import { useServiceRequests, useApproveGuestRequest } from '../hooks/useServiceRequest';
import { useProperties }      from '../hooks/useProperties';
import { useWallet }          from '../hooks/useWallet';
import { SERVICES }           from '../data/mockData';

/* ── Normalise API request ──────────────────────────────────────────────────── */
const SERVICE_META = {
  plumbing:    { icon: '🔧', label: 'Plumbing'    },
  electricity: { icon: '⚡', label: 'Electrician' },
  cleaning:    { icon: '🧹', label: 'Cleaning'    },
};
const STATUS_MAP = {
  pending:     'Pending',
  in_progress: 'In Progress',
  completed:   'Completed',
  rejected:    'Rejected',
};
const formatScheduledAt = (iso) =>
  new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const normalize = (r) => {
  const meta   = SERVICE_META[r.service_type] ?? { icon: '🔧', label: r.service_type ?? '—' };
  const status = STATUS_MAP[r.status] ?? 'Pending';
  const date   = r.created_at
    ? new Date(r.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;
  return { id: r.id, title: r.issue ?? meta.label, category: meta.label, icon: meta.icon, status, date, isGuestRequest: r.is_approved === 0, scheduleType: r.schedule_type ?? 'now', scheduledAt: r.scheduled_at ?? null };
};

/* ── Stat Card ──────────────────────────────────────────────────────────────── */
const StatCard = ({ stat, delay }) => {
  const isAccent = stat.accent;
  return (
    <div
      className={`rounded-2xl p-4 card-shadow press-scale animate-fadeUp overflow-hidden relative border ${isAccent ? 'border-violet-200 col-span-2' : 'bg-surface-800 border-surface-500'}`}
      style={{
        animationDelay: delay,
        animationFillMode: 'forwards',
        opacity: 0,
        ...(isAccent ? { background: 'linear-gradient(135deg, rgba(124,58,237,0.08) 0%, rgba(167,139,250,0.06) 100%)' } : {}),
      }}
    >
      {isAccent ? (
        <div className="flex items-center justify-between">
          <div>
            <p className="font-syne font-extrabold text-2xl leading-none" style={{ color: '#7c3aed' }}>{stat.value}</p>
            <p className="font-dm text-xs mt-1 leading-tight" style={{ color: '#7c3aed' }}>{stat.label}</p>
          </div>
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.2)' }}>
            <Icon name={stat.icon} size={18} style={{ color: '#7c3aed' }} />
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-surface-700">
              <Icon name={stat.icon} size={18} className="text-brand-500" />
            </div>
          </div>
          <p className="font-syne font-extrabold text-slate-800 text-2xl leading-none">{stat.value}</p>
          <p className="font-dm text-surface-200 text-xs mt-1 leading-tight">{stat.label}</p>
        </>
      )}
    </div>
  );
};

/* ── Section Header ─────────────────────────────────────────────────────────── */
const SectionHeader = ({ title, badge, action, onAction }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      <div className="w-[3px] h-4 rounded-full bg-brand-500" />
      <h3 className="font-syne font-bold text-slate-800 text-sm">{title}</h3>
      {badge != null && badge > 0 && (
        <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-brand-500 text-white">
          {badge}
        </span>
      )}
    </div>
    {action && (
      <button onClick={onAction} className="font-dm text-xs text-brand-500 hover:text-brand-400 transition-colors">
        {action} →
      </button>
    )}
  </div>
);

/* ── Quick Book Service Card ─────────────────────────────────────────────────── */
const ServiceCard = ({ svc, onClick, delay }) => (
  <button
    onClick={onClick}
    className="flex-shrink-0 flex flex-col gap-2.5 p-4 rounded-2xl press-scale animate-fadeUp text-left w-[180px] bg-surface-800 border border-surface-500 card-shadow"
    style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
  >
    <div className="w-9 h-9 rounded-xl bg-surface-700 flex items-center justify-center text-lg">
      {svc.icon}
    </div>
    <div className="flex-1">
      <p className="font-syne font-bold text-slate-800 text-sm leading-tight">{svc.label}</p>
      <p className="font-dm text-sm text-surface-200 mt-0.5 leading-tight line-clamp-2">{svc.description}</p>
    </div>
    <div className="flex items-center gap-1">
      <span className="font-dm text-xs font-semibold text-brand-500">Book now</span>
      <Icon name="arrow" size={9} className="text-brand-500" />
    </div>
  </button>
);

/* ── Ongoing Card ────────────────────────────────────────────────────────────── */
const STATUS_BADGE = {
  'Pending':     { badge: 'orange', msg: 'Awaiting admin review' },
  'In Progress': { badge: 'blue',   msg: 'Team is working on it' },
};
const OngoingCard = ({ req, delay }) => {
  const meta = STATUS_BADGE[req.status] ?? STATUS_BADGE['Pending'];
  return (
    <div
      className="bg-surface-800 rounded-2xl card-shadow press-scale animate-fadeUp flex-shrink-0 w-[240px] overflow-hidden border border-surface-500"
      style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
    >
      <div className="h-[3px] rounded-t-2xl bg-brand-500" />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0 bg-surface-700">
              {req.icon}
            </div>
            <div>
              <p className="font-syne font-bold text-slate-800 text-md leading-tight line-clamp-1">{req.title}</p>
              <p className="font-dm text-surface-200 text-xs">{req.category}</p>
            </div>
          </div>
          <Badge label={req.status} color={meta.badge} />
        </div>

        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-700">
          <Icon name="clock" size={11} className="text-surface-300 flex-shrink-0" />
          <p className="font-dm text-xs text-surface-200 leading-tight">{meta.msg}</p>
        </div>

        {req.date && (
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-surface-500">
            <Icon name="clock" size={10} className="text-surface-300 flex-shrink-0" />
            <span className="font-dm text-xs text-surface-300">{req.date}</span>
          </div>
        )}
        {req.scheduleType === 'later' && req.scheduledAt && (
          <div className="flex items-center gap-1.5 mt-2">
            <Icon name="clock" size={10} className="text-brand-400 flex-shrink-0" />
            <span className="font-dm text-xs text-brand-500 font-medium">
              {formatScheduledAt(req.scheduledAt)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Guest Approval Card ─────────────────────────────────────────────────────── */
const GuestApprovalCard = ({ req, delay }) => {
  const { mutate: approve, isPending: isApproving } = useApproveGuestRequest();
  return (
    <div
      className="bg-surface-800 rounded-2xl card-shadow animate-fadeUp border border-violet-200 overflow-hidden"
      style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
    >
      <div className="h-[3px] rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #7c3aed, #a78bfa)' }} />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
            style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.18)' }}>
            {req.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-syne font-bold text-slate-800 text-md truncate">{req.title}</p>
            <p className="font-dm text-surface-200 text-sm">{req.category}</p>
            {req.date && (
              <div className="flex items-center gap-1 mt-0.5">
                <Icon name="clock" size={10} className="text-surface-300" />
                <span className="font-dm text-xs text-surface-300">{req.date}</span>
              </div>
            )}
            {req.scheduleType === 'later' && req.scheduledAt && (
              <div className="flex items-center gap-1 mt-0.5">
                <Icon name="clock" size={10} className="text-brand-400" />
                <span className="font-dm text-xs text-brand-500 font-medium">{formatScheduledAt(req.scheduledAt)}</span>
              </div>
            )}
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.14)' }}>
          <div className="flex items-center gap-1.5 min-w-0">
            <Icon name="user" size={12} className="flex-shrink-0" style={{ color: '#7c3aed' }} />
            <p className="font-dm text-sm text-violet-700 truncate">Guest request</p>
          </div>
          <button
            onClick={() => approve(req.id)}
            disabled={isApproving}
            className="flex-shrink-0 px-3 py-1.5 rounded-lg font-dm font-semibold text-sm text-white disabled:opacity-50 transition-opacity"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #a78bfa)' }}
          >
            {isApproving ? 'Approving…' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Completed Card ──────────────────────────────────────────────────────────── */
const CompletedCard = ({ req, delay }) => (
  <div
    className="bg-surface-800 rounded-2xl p-4 card-shadow press-scale animate-fadeUp border border-surface-500 overflow-hidden"
    style={{ animationDelay: delay, animationFillMode: 'forwards', opacity: 0 }}
  >
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg bg-surface-700">
        {req.icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-syne font-bold text-slate-800 text-sm truncate">{req.title}</p>
        <p className="font-dm text-surface-200 text-xs mt-0.5">{req.category}</p>
        {req.date && <p className="font-dm text-xs text-surface-300 mt-0.5">{req.date}</p>}
        {req.scheduleType === 'later' && req.scheduledAt && (
          <p className="font-dm text-xs text-brand-500 mt-0.5">{formatScheduledAt(req.scheduledAt)}</p>
        )}
      </div>
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-surface-700">
        <Icon name="check" size={12} className="text-brand-500" />
      </div>
    </div>
  </div>
);

/* ── Skeleton / Empty ────────────────────────────────────────────────────────── */
const SkeletonStatCard = () => (
  <div className="bg-surface-800 rounded-2xl p-4 border border-surface-500 card-shadow">
    <div className="w-10 h-10 rounded-xl shimmer-bg mb-3" />
    <div className="h-7 w-10 rounded-lg shimmer-bg mb-2" />
    <div className="h-3 w-20 rounded-md shimmer-bg" />
  </div>
);
const SkeletonOngoingCard = () => (
  <div className="flex-shrink-0 w-[240px] bg-surface-800 rounded-2xl border border-surface-500 card-shadow overflow-hidden">
    <div className="h-[3px] shimmer-bg" />
    <div className="p-4">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-10 h-10 rounded-xl shimmer-bg flex-shrink-0" />
        <div className="flex-1">
          <div className="h-3.5 w-24 rounded-md shimmer-bg mb-1.5" />
          <div className="h-2.5 w-16 rounded-md shimmer-bg" />
        </div>
        <div className="w-16 h-5 rounded-full shimmer-bg" />
      </div>
      <div className="h-8 rounded-xl shimmer-bg" />
    </div>
  </div>
);
const SkeletonCompletedCard = () => (
  <div className="bg-surface-800 rounded-2xl p-4 border border-surface-500 card-shadow">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl shimmer-bg flex-shrink-0" />
      <div className="flex-1">
        <div className="h-3.5 w-32 rounded-md shimmer-bg mb-1.5" />
        <div className="h-2.5 w-20 rounded-md shimmer-bg" />
      </div>
      <div className="w-7 h-7 rounded-full shimmer-bg" />
    </div>
  </div>
);
const EmptyInline = ({ emoji, label }) => (
  <div className="flex flex-col items-center justify-center py-10 text-center w-full">
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3 text-2xl"
      style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.14)' }}
    >
      {emoji}
    </div>
    <p className="font-syne font-semibold text-slate-700 text-sm">Nothing here yet</p>
    <p className="font-dm text-xs text-surface-200 mt-0.5">{label}</p>
  </div>
);

/* ── Main Page ───────────────────────────────────────────────────────────────── */
const DashboardPage = () => {
  const { user }         = useAuth();
  const { setActiveTab, setPreselectedService } = useApp();

  const { data: reqData,    isLoading: reqLoading,    refetch: refetchReqs    } = useServiceRequests();
  const { data: propData,   isLoading: propLoading,   refetch: refetchProps   } = useProperties();
  const { data: walletData, isLoading: walletLoading, refetch: refetchWallet  } = useWallet();

  const walletBalance  = walletData?.wallet?.balance;
  const walletCurrency = walletData?.wallet?.currency ?? 'GHS';
  const walletTransactions = walletData?.transactions ?? [];

  const handleRefresh = () => Promise.all([refetchReqs(), refetchProps(), refetchWallet()]);

  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const lsUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  }, []);

  const resolveFullName = (u) => {
    if (!u) return null;
    if (u.name) return u.name;
    if (u.full_name) return u.full_name;
    if (u.first_name || u.last_name) return [u.first_name, u.last_name].filter(Boolean).join(' ');
    if (u.username) return u.username;
    return null;
  };
  const displayName    = resolveFullName(user) || resolveFullName(lsUser) || 'User';
  const avatarInitials = [
    (user?.first_name || lsUser?.first_name || displayName)?.[0],
    (user?.last_name  || lsUser?.last_name),
  ].filter(Boolean).map((s) => s[0]).join('').toUpperCase() || 'U';
  const address        = user?.address    || lsUser?.address    || lsUser?.location   || '';
  const tier           = user?.tier       || lsUser?.tier       || 'Gold Member';
  const memberSince    = user?.memberSince || lsUser?.memberSince || lsUser?.created_at?.slice(0, 4) || '2024';

  /* Derived stats */
  const rawRequests   = reqData?.data ?? reqData?.serviceRequests ?? [];
  const allRequests   = rawRequests.map(normalize);
  const properties    = propData?.properties ?? propData ?? [];
  const guestApprovalList = allRequests.filter((r) => r.isGuestRequest && r.status === 'Pending');
  const ongoingList       = allRequests.filter((r) => r.status === 'In Progress');
  const completedList     = allRequests.filter((r) => r.status === 'Completed');

  const totalSpent = walletTransactions
    .filter((t) => t.type === 'debit')
    .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
  const spentDisplay = walletLoading
    ? '—'
    : `${walletCurrency} ${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const stats = [
    { id: 1, label: 'Total Requests', value: reqLoading  ? '—' : String(allRequests.length),  icon: 'list'     },
    { id: 2, label: 'Completed',      value: reqLoading  ? '—' : String(completedList.length), icon: 'check'    },
    { id: 3, label: 'Ongoing',        value: reqLoading  ? '—' : String(ongoingList.length), icon: 'clock' },
    { id: 4, label: 'Properties',     value: propLoading ? '—' : String(properties.length),    icon: 'building' },
    { id: 5, label: 'Guest Service Request Approval Pending', value: reqLoading ? '—' : String(guestApprovalList.length), icon: 'user', accent: true },
  ];

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="pb-8">

        {/* ── Welcome banner ──────────────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-4">
          <div
            className="rounded-3xl p-5 relative overflow-hidden animate-fadeUp card-shadow"
            style={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)',
              border: '1px solid rgba(99,102,241,0.4)',
              animationFillMode: 'forwards',
            }}
          >
            <div className="noise-overlay rounded-3xl" />
            <div className="absolute -top-8 -right-8 w-44 h-44 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.15), transparent 70%)' }} />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08), transparent 70%)' }} />

            <div className="relative z-10 flex items-start justify-between">
              <div>
                <p className="font-dm text-indigo-200 text-xs">{greeting} 👋</p>
                <h2 className="font-syne font-bold text-white text-xl mt-0.5">{displayName}</h2>
                {address ? (
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Icon name="location" size={11} style={{ color: 'rgba(199,210,254,0.85)' }} />
                    <p className="font-dm text-xs truncate max-w-[170px]" style={{ color: 'rgba(199,210,254,0.85)' }}>{address}</p>
                  </div>
                ) : null}
              </div>
              <Avatar initials={user?.avatar || avatarInitials} size="xl" ring />
            </div>

            {/* Balance + Spent */}
            <div className="relative z-10 mt-4 grid grid-cols-2 gap-2">
              <button
                onClick={() => setActiveTab('wallet')}
                className="rounded-2xl p-3 text-left press-scale"
                style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                <p className="font-dm text-indigo-200 text-xs uppercase tracking-wider">Wallet Balance</p>
                {walletLoading ? (
                  <div className="h-6 w-24 rounded-lg mt-1.5 shimmer-bg opacity-40" />
                ) : (
                  <p className="font-syne font-extrabold text-white text-lg leading-tight mt-0.5">
                    {walletCurrency}{' '}
                    {walletBalance != null
                      ? Number(walletBalance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '0.00'}
                  </p>
                )}
              </button>
              <div className="rounded-2xl p-3" style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)' }}>
                <p className="font-dm text-indigo-200 text-xs uppercase tracking-wider">Total Spent</p>
                <p className="font-syne font-extrabold text-white text-lg leading-tight mt-0.5">{spentDisplay}</p>
              </div>
            </div>

            {/* Tier + member since */}
            <div className="relative z-10 mt-2 flex items-center gap-2">
              <span className="font-dm text-xs" style={{ color: 'rgba(199,210,254,0.7)' }}>Member since {memberSince}</span>
            </div>
          </div>
        </div>

        {/* ── Stats grid ──────────────────────────────────────────────────── */}
        <div className="px-5 mb-6">
          <SectionHeader title="Overview" />
          <div className="grid grid-cols-2 gap-3">
            {reqLoading || propLoading
              ? [1,2,3,4].map((n) => <SkeletonStatCard key={n} />)
              : stats.map((s, i) => <StatCard key={s.id} stat={s} delay={`${0.1 + i * 0.07}s`} />)
            }
          </div>
        </div>

        {/* ── Quick Book ──────────────────────────────────────────────────── */}
        <div className="mb-6">
          <div className="px-5"><SectionHeader title="Quick Book" /></div>
          <div className="flex gap-3 px-5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {SERVICES.map((svc, i) => (
              <ServiceCard
                key={svc.id}
                svc={svc}
                onClick={() => { setPreselectedService(svc); setActiveTab('new'); }}
                delay={`${0.05 * i}s`}
              />
            ))}
          </div>
        </div>

        {/* ── Guest Approvals ─────────────────────────────────────────────── */}
        {(reqLoading || guestApprovalList.length > 0) && (
          <div className="px-5 mb-6">
            <SectionHeader
              title="Guest Approvals"
              badge={reqLoading ? null : guestApprovalList.length}
              action="See all"
              onAction={() => setActiveTab('requests')}
            />
            <div className="flex flex-col gap-3">
              {reqLoading
                ? [1, 2].map((n) => <SkeletonCompletedCard key={n} />)
                : guestApprovalList.map((r, i) => (
                    <GuestApprovalCard key={r.id} req={r} delay={`${0.08 * i}s`} />
                  ))
              }
            </div>
          </div>
        )}

        {/* ── Ongoing requests ────────────────────────────────────────────── */}
        <div className="mb-6">
          <div className="px-5">
            <SectionHeader
              title="Ongoing"
              badge={reqLoading ? null : ongoingList.length}
              action="See all"
              onAction={() => setActiveTab('requests')}
            />
          </div>
          <div className="flex gap-3 px-5 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {reqLoading
              ? [1, 2].map((n) => <SkeletonOngoingCard key={n} />)
              : ongoingList.length === 0
                ? <EmptyInline emoji="🔍" label="No active or pending requests" />
                : ongoingList.map((r, i) => <OngoingCard key={r.id} req={r} delay={`${0.08 * i}s`} />)
            }
          </div>
        </div>

        {/* ── Completed requests ──────────────────────────────────────────── */}
        <div className="px-5">
          <SectionHeader title="Completed" action="See all" onAction={() => setActiveTab('requests')} />
          <div className="flex flex-col gap-3">
            {reqLoading
              ? [1, 2, 3].map((n) => <SkeletonCompletedCard key={n} />)
              : completedList.length === 0
                ? <EmptyInline emoji="✅" label="Completed requests will appear here" />
                : completedList.slice(0, 3).map((r, i) => <CompletedCard key={r.id} req={r} delay={`${0.08 * i}s`} />)
            }
          </div>
        </div>

      </div>
    </PullToRefresh>
  );
};

export default DashboardPage;
